import { useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { clearCachedCurrentUserProfile } from "@/lib/profileCache";
import { clearNavCache } from "@/lib/navCache";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let initialised = false;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          Sentry.setUser({ id: session.user.id });
        } else {
          Sentry.setUser(null);
        }
        // Only clear loading from listener if getSession already ran
        if (initialised) {
          setIsLoading(false);
        }
      }
    );

    // Resolve current session — this is the single source of truth for initial load
    supabase.auth.getSession().then(({ data: { session } }) => {
      initialised = true;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        Sentry.setUser({ id: session.user.id });
      } else {
        Sentry.setUser(null);
      }
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      clearCachedCurrentUserProfile();
      clearNavCache();
      Sentry.setUser(null);
    } catch {
      setUser(null);
      setSession(null);
      clearCachedCurrentUserProfile();
      clearNavCache();
      Sentry.setUser(null);
    }
  };

  return {
    user,
    session,
    isLoading,
    signOut,
    isAuthenticated: !!user,
  };
};
